// ProductDetail.jsx - COMPLETE UPDATED VERSION
import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import './ProductDetail.css';
import productsService from '../../../services/products.service';
import Header from '../../../components/layout/Header/Header';
import Footer from '../../../components/layout/Footer/Footer';
import OrderForm from './OrderForm';

const ProductDetailsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [relatedProducts, setRelatedProducts] = useState([]);

  const [selectedImage, setSelectedImage] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [isFavorite, setIsFavorite] = useState(false);
  const [activeTab, setActiveTab] = useState('description');
  const [showImageModal, setShowImageModal] = useState(false);
  const [showOrderForm, setShowOrderForm] = useState(false);

  const [selectedVariantIndex, setSelectedVariantIndex] = useState(0);

  useEffect(() => {
    if (id) {
      fetchProduct(id);
    }
  }, [id]);

  const fetchProduct = async (productId) => {
    setLoading(true);
    setError(null);
    try {
      const result = await productsService.getProductById(productId);
      if (result.success && result.product) {
        setProduct(result.product);
        setSelectedVariantIndex(0);

        if (result.product.category) {
          fetchRelatedProducts(result.product.category, productId);
        }
      } else {
        setError('Produit non trouvé');
      }
    } catch (err) {
      setError('Une erreur est survenue');
      console.error('Error fetching product:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchRelatedProducts = async (category, currentProductId) => {
    try {
      const result = await productsService.getProductsByCategory(category);
      if (result.success) {
        const related = result.products
          .filter(p => p.id !== currentProductId)
          .slice(0, 4);
        setRelatedProducts(related);
      }
    } catch (err) {
      console.error('Error fetching related products:', err);
    }
  };

  const formatDimensions = (dimensions) => {
    if (!dimensions) return '';
    const parts = [];
    if (dimensions.width) parts.push(`L${dimensions.width}`);
    if (dimensions.height) parts.push(`H${dimensions.height}`);
    if (dimensions.length) parts.push(`P${dimensions.length}`);
    return parts.join(' × ') + ' cm';
  };

  const getCurrentPrice = () => {
    if (product.hasVariants && product.variants && product.variants[selectedVariantIndex]) {
      return product.variants[selectedVariantIndex].price;
    }
    return product.price;
  };

  const getSelectedVariant = () => {
    if (product.hasVariants && product.variants && product.variants[selectedVariantIndex]) {
      return product.variants[selectedVariantIndex];
    }
    return null;
  };

  const formatPrice = (price) => {
    return price?.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ') || '0';
  };

  const renderStars = (rating) => {
    const stars = [];
    const fullStars = Math.floor(rating || 0);
    for (let i = 1; i <= 5; i++) {
      stars.push(
        <span key={i} className={`pdp-star ${i <= fullStars ? '' : 'empty'}`}>★</span>
      );
    }
    return stars;
  };

  const getProductImages = () => {
    if (Array.isArray(product.images) && product.images.length > 0) {
      return product.images.map(img =>
        typeof img === 'object' && img.url ? img.url : img
      );
    }
    if (product.image) {
      if (typeof product.image === 'object' && product.image.url) {
        return [product.image.url];
      }
      if (typeof product.image === 'string') {
        return [product.image];
      }
    }
    return ['/placeholder-image.jpg'];
  };

  // UPDATED: Get unique colors from variants (handle arrays)
  const uniqueColors = useMemo(() => {
    if (!product?.variants) return [];
    const colors = new Set();
    product.variants.forEach(v => {
      if (Array.isArray(v.colors)) {
        v.colors.forEach(c => colors.add(c));
      } else if (v.color) {
        colors.add(v.color);
      }
    });
    return Array.from(colors);
  }, [product]);

  // UPDATED: Get unique materials from variants (handle arrays)
  const uniqueMaterials = useMemo(() => {
    if (!product?.variants) return [];
    const materials = new Set();
    product.variants.forEach(v => {
      if (Array.isArray(v.materials)) {
        v.materials.forEach(m => materials.add(m));
      } else if (v.material) {
        materials.add(v.material);
      }
    });
    return Array.from(materials);
  }, [product]);

  // Get unique compartiments
  const uniqueCompartiments = useMemo(() => {
    if (!product?.variants) return [];
    const compartiments = new Set();
    product.variants.forEach(v => {
      if (v.compartiment) compartiments.add(v.compartiment);
    });
    return Array.from(compartiments);
  }, [product]);

  const handleWhatsAppClick = () => {
    const currentPrice = getCurrentPrice();
    const variant = getSelectedVariant();
    
    let message = `Bonjour, je suis intéressé par le produit:\n${product.name}\nPrix: ${formatPrice(currentPrice)} DA`;
    
    if (variant) {
      message += `\n\nVariante: ${variant.name}`;
    }
    
    const whatsappUrl = `https://wa.me/213560605655?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  const handleRelatedProductClick = (productId) => {
    navigate(`/product/${productId}`);
    window.scrollTo(0, 0);
  };

  if (loading) {
    return (
      <div className="pdp-wrapper">
        <Header />
        <div className="pdp-container">
          <div style={{ textAlign: 'center', padding: '60px 0' }}>
            <p>Chargement...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="pdp-wrapper">
        <Header />
        <div className="pdp-container">
          <div style={{ textAlign: 'center', padding: '60px 0' }}>
            <h2>Erreur</h2>
            <p>{error || 'Produit non trouvé'}</p>
            <button onClick={() => navigate('/products')}>Retour aux produits</button>
          </div>
        </div>
      </div>
    );
  }

  const productImages = getProductImages();
  const currentImage = productImages[selectedImage];
  const currentPrice = getCurrentPrice();
  const selectedVariant = getSelectedVariant();
  const discount = product.oldPrice && !product.hasVariants
    ? Math.round(((product.oldPrice - product.price) / product.oldPrice) * 100)
    : 0;

  return (
    <>
      <Header />
      <div className="classjustforheadercolor"></div>

      <div className="pdp-wrapper">
        <div className="pdp-container">
          {/* Breadcrumb */}
          <nav className="pdp-breadcrumb">
            <a href="/" className="pdp-breadcrumb__link">Accueil</a>
            <span className="pdp-breadcrumb__separator">›</span>
            <a href="/products" className="pdp-breadcrumb__link">Produits</a>
            <span className="pdp-breadcrumb__separator">›</span>
            <span className="pdp-breadcrumb__current">{product.name}</span>
          </nav>

          {/* Main Grid */}
          <div className="pdp-grid">
            {/* Left Column */}
            <div className="pdp-left-col">
              {/* Gallery */}
              <div className="pdp-gallery">
                <div className="pdp-gallery__main" onClick={() => setShowImageModal(true)}>
                  {product.badge && <div className="pdp-gallery__badge">{product.badge}</div>}
                  {discount > 0 && <div className="pdp-gallery__discount">-{discount}%</div>}
                  <img src={currentImage} alt={product.name} className="pdp-gallery__image" />
                  {productImages.length > 1 && (
                    <>
                      <button
                        className="pdp-gallery__nav pdp-gallery__nav--prev"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedImage((selectedImage - 1 + productImages.length) % productImages.length);
                        }}
                      >
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <polyline points="15 18 9 12 15 6" />
                        </svg>
                      </button>
                      <button
                        className="pdp-gallery__nav pdp-gallery__nav--next"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedImage((selectedImage + 1) % productImages.length);
                        }}
                      >
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <polyline points="9 18 15 12 9 6" />
                        </svg>
                      </button>
                    </>
                  )}
                </div>

                {productImages.length > 1 && (
                  <div className="pdp-gallery__thumbs">
                    {productImages.map((image, idx) => (
                      <button
                        key={idx}
                        onClick={() => setSelectedImage(idx)}
                        className={`pdp-gallery__thumb ${selectedImage === idx ? 'active' : ''}`}
                      >
                        <img src={image} alt={`View ${idx + 1}`} />
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Product Info */}
              <div className="pdp-info-card">
                <h1 className="pdp-info__title">{product.name}</h1>

                <div className="pdp-info__rating">
                  <div className="pdp-stars">{renderStars(product.rating)}</div>
                  <span className="pdp-rating-count">({product.reviews || 0} avis)</span>
                </div>

                <div className="pdp-info__price">
                  <span className="pdp-price-current">{formatPrice(currentPrice)} DA</span>
                  {product.oldPrice && !product.hasVariants && (
                    <span className="pdp-price-old">{formatPrice(product.oldPrice)} DA</span>
                  )}
                </div>

                {/* UPDATED: Variant Selector with array support */}
                {product.hasVariants && product.variants && product.variants.length > 0 && (
                  <div className="pdp-variants">
                    <label className="pdp-variant-label">
                      Choisissez une variante:
                    </label>
                    <div className="pdp-variant-options-single">
                      {product.variants.map((variant, index) => (
                        <button
                          key={index}
                          className={`pdp-variant-card ${selectedVariantIndex === index ? 'active' : ''}`}
                          onClick={() => setSelectedVariantIndex(index)}
                        >
                          <div className="pdp-variant-card__name">
                            {variant.name}
                          </div>
                          <div className="pdp-variant-card__details">
                            {variant.dimensions && (
                              <span className="pdp-variant-detail">
                                📏 {formatDimensions(variant.dimensions)}
                              </span>
                            )}
                            {/* UPDATED: Display multiple colors */}
                            {Array.isArray(variant.colors) && variant.colors.length > 0 && (
                              <span className="pdp-variant-detail">
                                🎨 {variant.colors.join(' + ')}
                              </span>
                            )}
                            {/* Backward compatibility: single color */}
                            {variant.color && !variant.colors && (
                              <span className="pdp-variant-detail">
                                🎨 {variant.color}
                              </span>
                            )}
                            {/* UPDATED: Display multiple materials */}
                            {Array.isArray(variant.materials) && variant.materials.length > 0 && (
                              <span className="pdp-variant-detail">
                                🔨 {variant.materials.join(' + ')}
                              </span>
                            )}
                            {/* Backward compatibility: single material */}
                            {variant.material && !variant.materials && (
                              <span className="pdp-variant-detail">
                                🔨 {variant.material}
                              </span>
                            )}
                            {variant.compartiment && (
                              <span className="pdp-variant-detail">
                                📦 {variant.compartiment}
                              </span>
                            )}
                          </div>
                          <div className="pdp-variant-card__price">
                            {formatPrice(variant.price)} DA
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                <div className="pdp-info__quantity">
                  <label className="pdp-quantity-label">Quantité</label>
                  <div className="pdp-quantity-selector">
                    <button
                      className="pdp-quantity-btn"
                      onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    >
                      -
                    </button>
                    <input
                      type="number"
                      value={quantity}
                      onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                      className="pdp-quantity-input"
                    />
                    <button
                      className="pdp-quantity-btn"
                      onClick={() => setQuantity(Math.min(product.stockCount || 999, quantity + 1))}
                    >
                      +
                    </button>
                  </div>
                </div>

                <div className="pdp-trust-badges">
                  <div className="pdp-trust-badge">
                    <div className="pdp-trust-icon">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <rect x="1" y="3" width="15" height="13" />
                        <polygon points="16 8 20 8 23 11 23 16 16 16 16 8" />
                        <circle cx="5.5" cy="18.5" r="2.5" />
                        <circle cx="18.5" cy="18.5" r="2.5" />
                      </svg>
                    </div>
                    <div className="pdp-trust-text">
                      <div className="pdp-trust-title">Livraison Disponible</div>
                      <div className="pdp-trust-subtitle">Partout en Algérie</div>
                    </div>
                  </div>
                  <div className="pdp-trust-badge">
                    <div className="pdp-trust-icon">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                      </svg>
                    </div>
                    <div className="pdp-trust-text">
                      <div className="pdp-trust-title">Garantie Du Qalité</div>
                      <div className="pdp-trust-subtitle">Fabricant</div>
                    </div>
                  </div>

                </div>
              </div>

              {/* Tabs */}
              <div className="pdp-tabs">
                <div className="pdp-tabs__header">
                  <button
                    onClick={() => setActiveTab('description')}
                    className={`pdp-tabs__btn ${activeTab === 'description' ? 'active' : ''}`}
                  >
                    Description
                  </button>
                </div>

                <div className="pdp-tabs__content">
                  {activeTab === 'description' && (
                    <div>
                      <p className="pdp-tab-desc__text">{product.description}</p>
                      {product.features && product.features.length > 0 && (
                        <div className="pdp-features-grid">
                          {product.features.map((feature, idx) => (
                            <div key={idx} className="pdp-feature-item">
                              <svg className="pdp-feature-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <polyline points="20 6 9 17 4 12" />
                              </svg>
                              <span>{feature}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {activeTab === 'reviews' && (
                    <div>
                      <p style={{ color: 'var(--color-text-tertiary)', fontSize: '0.875rem' }}>
                        Aucun avis pour le moment
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Right Column - Order Card */}
            <div className="pdp-right-col">
              <OrderForm
                product={{
                  ...product,
                  price: currentPrice,
                  selectedVariant: selectedVariant,
                  selectedVariantIndex: selectedVariantIndex,
                  quantity: quantity,
                  images: product.images || [product.image]
                }}
                onClose={() => {}}
              />
            </div>
          </div>

          {/* Similar Products */}
          {relatedProducts.length > 0 && (
            <div className="pdp-similar">
              <h2 className="pdp-similar__title">Produits similaires</h2>
              <div className="pdp-similar__grid">
                {relatedProducts.map((item) => {
                  const itemImages = Array.isArray(item.images) && item.images.length > 0
                    ? (typeof item.images[0] === 'object' ? item.images[0].url : item.images[0])
                    : (typeof item.image === 'object' ? item.image.url : item.image);

                  return (
                    <div
                      key={item.id}
                      className="pdp-similar-card"
                      onClick={() => handleRelatedProductClick(item.id)}
                    >
                      <div className="pdp-similar-card__image">
                        <img
                          src={itemImages}
                          alt={item.name}
                          onError={(e) => e.target.src = '/placeholder-image.jpg'}
                        />
                      </div>
                      <div className="pdp-similar-card__content">
                        <h3 className="pdp-similar-card__name">{item.name}</h3>
                        <div className="pdp-similar-card__rating">
                          {renderStars(item.rating)}
                        </div>
                        <div className="pdp-similar-card__price-row">
                          <span className="pdp-similar-card__price">
                            {item.displayPriceText || `${formatPrice(item.price)} DA`}
                          </span>
                          {item.oldPrice && !item.hasVariants && (
                            <span className="pdp-similar-card__old-price">{formatPrice(item.oldPrice)} DA</span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Image Modal */}
        {showImageModal && (
          <div className="pdp-modal" onClick={() => setShowImageModal(false)}>
            <button className="pdp-modal__close">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
            <img src={currentImage} alt={product.name} className="pdp-modal__image" />
          </div>
        )}

        {/* Order Form Modal */}
        {showOrderForm && (
          <OrderForm
            product={{
              ...product,
              price: currentPrice,
              selectedVariant: selectedVariant,
              quantity: quantity
            }}
            onClose={() => setShowOrderForm(false)}
          />
        )}
      </div>
      <Footer />
    </>
  );
};

export default ProductDetailsPage;